import { PARTICIPANT_FLAG_SYSTEM } from '#src/codeSystems.ts'

export const ParticipantFlagCode = {
  ALLERGIES: 'allergies',
  ASSISTIVE_DEVICE: 'assistive-device',
  FOUR_WHEEL_WALKER: 'four-wheel-walker',
  WHEELCHAIR: 'wheelchair',
  ADVANCED_DIRECTIVE: 'advanced-directive',
  BEHAVIORAL_ISSUES: 'behavioral-issues',
  DEMENTIA: 'dementia',
  DNR: 'dnr',
  DNI: 'dni',
  FULL_CODE: 'full-code',
  CPR_OK: 'cpr-ok',
  ESCORT_REQUIREMENT: 'escort-requirement',
  FAMILY_ESCORT: 'family-escort',
  HIGH_FALL_RISK: 'high-fall-risk',
  IMMUNIZATIONS_DUE: 'immunizations-due',
  INCONTINENT: 'incontinent',
  INTERPRETER_NEEDS: 'interpreter-needs',
  ISOLATION_PRECAUTIONS: 'isolation-precautions',
  NKDA: 'nkda',
  OVERDUE_MEDICATIONS: 'overdue-medications',
  SMOKING_STATUS: 'smoking-status',
  ELOPEMENT_RISK: 'elopement-risk',
  HEARING_IMPAIRMENT: 'hearing-impairment',
  VISION_IMPAIRMENT: 'vision-impairment',
  OOSA: 'oosa',
  CRITICAL_ATTENTION: 'critical-attention',
  ISOLATION_REQUIRED: 'isolation-required',
  ORBIT_NATIVE: 'orbit-native',
  CB_PCP: 'cb-pcp',
  HIGH_RISK: 'high-risk',
  MEDIUM_RISK: 'medium-risk',
  LOW_RISK: 'low-risk',
} as const
export type ParticipantFlagCode =
  (typeof ParticipantFlagCode)[keyof typeof ParticipantFlagCode]

export const ParticipantFlagPriority = {
  P0: 'p0',
  P1: 'p1',
  P2: 'p2',
  P3: 'p3',
} as const
export type ParticipantFlagPriority =
  (typeof ParticipantFlagPriority)[keyof typeof ParticipantFlagPriority]

// Declaration order is the display order.
export const ParticipantFlagCategory = {
  RISK: 'Risk',
  CODE: 'Code',
  MEDICAL: 'Medical',
  ASSISTIVE_DEVICES: 'Assistive Devices',
  ESCORT: 'Escort',
  STATUS: 'Status',
} as const
export type ParticipantFlagCategory =
  (typeof ParticipantFlagCategory)[keyof typeof ParticipantFlagCategory]

export const PARTICIPANT_FLAG_CATEGORY_ORDER: readonly ParticipantFlagCategory[] =
  Object.values(ParticipantFlagCategory)

export interface ParticipantFlagDefinition {
  coding: {
    system: string
    code: string
    display: string
  }
  priority: ParticipantFlagPriority
  description?: string
}

export const PARTICIPANT_FLAGS: Record<
  ParticipantFlagCode,
  ParticipantFlagDefinition & { category: ParticipantFlagCategory }
> = {
  [ParticipantFlagCode.DNR]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.DNR,
      display: 'DNR',
    },
    category: ParticipantFlagCategory.CODE,
    priority: ParticipantFlagPriority.P0,
    description: 'Do Not Resuscitate',
  },
  [ParticipantFlagCode.DNI]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.DNI,
      display: 'DNI',
    },
    category: ParticipantFlagCategory.CODE,
    priority: ParticipantFlagPriority.P0,
    description: 'Do Not Intubate',
  },
  [ParticipantFlagCode.FULL_CODE]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.FULL_CODE,
      display: 'Full Code',
    },
    category: ParticipantFlagCategory.CODE,
    priority: ParticipantFlagPriority.P0,
    description: 'All resuscitation measures permitted',
  },
  [ParticipantFlagCode.CPR_OK]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.CPR_OK,
      display: 'CPR OK',
    },
    category: ParticipantFlagCategory.CODE,
    priority: ParticipantFlagPriority.P0,
    description: 'Cardiopulmonary resuscitation OK',
  },
  [ParticipantFlagCode.HIGH_RISK]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.HIGH_RISK,
      display: 'High Risk',
    },
    category: ParticipantFlagCategory.RISK,
    priority: ParticipantFlagPriority.P0,
    description: 'Acute clinical risk',
  },
  [ParticipantFlagCode.MEDIUM_RISK]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.MEDIUM_RISK,
      display: 'Medium Risk',
    },
    category: ParticipantFlagCategory.RISK,
    priority: ParticipantFlagPriority.P3,
    description: 'Moderate clinical risk',
  },
  [ParticipantFlagCode.LOW_RISK]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.LOW_RISK,
      display: 'Low Risk',
    },
    category: ParticipantFlagCategory.RISK,
    priority: ParticipantFlagPriority.P3,
    description: 'Low clinical risk',
  },
  [ParticipantFlagCode.ESCORT_REQUIREMENT]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.ESCORT_REQUIREMENT,
      display: 'Escort Required',
    },
    category: ParticipantFlagCategory.ESCORT,
    priority: ParticipantFlagPriority.P0,
  },
  [ParticipantFlagCode.FAMILY_ESCORT]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.FAMILY_ESCORT,
      display: 'Family Escort',
    },
    category: ParticipantFlagCategory.ESCORT,
    priority: ParticipantFlagPriority.P0,
  },
  [ParticipantFlagCode.INTERPRETER_NEEDS]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.INTERPRETER_NEEDS,
      display: 'Interpreter Required',
    },
    category: ParticipantFlagCategory.ESCORT,
    priority: ParticipantFlagPriority.P0,
  },
  [ParticipantFlagCode.ALLERGIES]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.ALLERGIES,
      display: 'Allergies',
    },
    category: ParticipantFlagCategory.MEDICAL,
    priority: ParticipantFlagPriority.P0,
  },
  [ParticipantFlagCode.HIGH_FALL_RISK]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.HIGH_FALL_RISK,
      display: 'High Fall Risk',
    },
    category: ParticipantFlagCategory.RISK,
    priority: ParticipantFlagPriority.P0,
  },
  [ParticipantFlagCode.ISOLATION_PRECAUTIONS]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.ISOLATION_PRECAUTIONS,
      display: 'Isolation',
    },
    category: ParticipantFlagCategory.RISK,
    priority: ParticipantFlagPriority.P0,
  },
  [ParticipantFlagCode.ELOPEMENT_RISK]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.ELOPEMENT_RISK,
      display: 'Wandering Risk',
    },
    category: ParticipantFlagCategory.RISK,
    priority: ParticipantFlagPriority.P0,
  },
  [ParticipantFlagCode.SMOKING_STATUS]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.SMOKING_STATUS,
      display: 'Smoker',
    },
    category: ParticipantFlagCategory.MEDICAL,
    priority: ParticipantFlagPriority.P0,
  },
  [ParticipantFlagCode.BEHAVIORAL_ISSUES]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.BEHAVIORAL_ISSUES,
      display: 'Behavioral',
    },
    category: ParticipantFlagCategory.MEDICAL,
    priority: ParticipantFlagPriority.P0,
  },
  [ParticipantFlagCode.DEMENTIA]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.DEMENTIA,
      display: 'Dementia',
    },
    category: ParticipantFlagCategory.MEDICAL,
    priority: ParticipantFlagPriority.P0,
  },
  [ParticipantFlagCode.WHEELCHAIR]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.WHEELCHAIR,
      display: 'Wheelchair',
    },
    category: ParticipantFlagCategory.ASSISTIVE_DEVICES,
    priority: ParticipantFlagPriority.P3,
  },
  [ParticipantFlagCode.FOUR_WHEEL_WALKER]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.FOUR_WHEEL_WALKER,
      display: '4WW',
    },
    category: ParticipantFlagCategory.ASSISTIVE_DEVICES,
    priority: ParticipantFlagPriority.P3,
    description: 'Four-wheel walker',
  },
  [ParticipantFlagCode.ASSISTIVE_DEVICE]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.ASSISTIVE_DEVICE,
      display: 'Other assistive devices',
    },
    category: ParticipantFlagCategory.ASSISTIVE_DEVICES,
    priority: ParticipantFlagPriority.P3,
  },
  [ParticipantFlagCode.ADVANCED_DIRECTIVE]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.ADVANCED_DIRECTIVE,
      display: 'Advanced Directive',
    },
    category: ParticipantFlagCategory.CODE,
    priority: ParticipantFlagPriority.P3,
  },
  [ParticipantFlagCode.OVERDUE_MEDICATIONS]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.OVERDUE_MEDICATIONS,
      display: 'Overdue Medications',
    },
    category: ParticipantFlagCategory.MEDICAL,
    priority: ParticipantFlagPriority.P3,
  },
  [ParticipantFlagCode.IMMUNIZATIONS_DUE]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.IMMUNIZATIONS_DUE,
      display: 'Immunizations Due',
    },
    category: ParticipantFlagCategory.MEDICAL,
    priority: ParticipantFlagPriority.P3,
  },
  [ParticipantFlagCode.NKDA]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.NKDA,
      display: 'NKDA',
    },
    category: ParticipantFlagCategory.MEDICAL,
    priority: ParticipantFlagPriority.P3,
    description: 'No Known Drug Allergies',
  },
  [ParticipantFlagCode.INCONTINENT]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.INCONTINENT,
      display: 'Incontinent',
    },
    category: ParticipantFlagCategory.MEDICAL,
    priority: ParticipantFlagPriority.P3,
  },
  [ParticipantFlagCode.HEARING_IMPAIRMENT]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.HEARING_IMPAIRMENT,
      display: 'Hearing Impairment',
    },
    category: ParticipantFlagCategory.MEDICAL,
    priority: ParticipantFlagPriority.P3,
  },
  [ParticipantFlagCode.VISION_IMPAIRMENT]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.VISION_IMPAIRMENT,
      display: 'Vision Impairment',
    },
    category: ParticipantFlagCategory.MEDICAL,
    priority: ParticipantFlagPriority.P3,
  },
  [ParticipantFlagCode.OOSA]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.OOSA,
      display: 'OOSA',
    },
    category: ParticipantFlagCategory.STATUS,
    priority: ParticipantFlagPriority.P0,
    description: 'Out of Service Area',
  },
  [ParticipantFlagCode.CRITICAL_ATTENTION]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.CRITICAL_ATTENTION,
      display: 'Critical Attention',
    },
    category: ParticipantFlagCategory.RISK,
    priority: ParticipantFlagPriority.P0,
  },
  [ParticipantFlagCode.ISOLATION_REQUIRED]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.ISOLATION_REQUIRED,
      display: 'Isolation Required',
    },
    category: ParticipantFlagCategory.RISK,
    priority: ParticipantFlagPriority.P0,
  },
  // A flag to drive features being hidden/disabled in Elation via sidecar
  [ParticipantFlagCode.ORBIT_NATIVE]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.ORBIT_NATIVE,
      display: 'Orbit Native',
    },
    category: ParticipantFlagCategory.STATUS,
    priority: ParticipantFlagPriority.P3,
    description: 'Chart managed natively in Orbit',
  },
  [ParticipantFlagCode.CB_PCP]: {
    coding: {
      system: PARTICIPANT_FLAG_SYSTEM,
      code: ParticipantFlagCode.CB_PCP,
      display: 'CB-PCP',
    },
    category: ParticipantFlagCategory.MEDICAL,
    priority: ParticipantFlagPriority.P3,
    description: 'Community-Based PCP',
  },
}

export const OOSA_REASON = {
  VACATION: 'vacation',
  VISITING_FAMILY: 'visiting-family',
  MEDICAL: 'medical',
  OTHER: 'other',
} as const
export type OosaReason = (typeof OOSA_REASON)[keyof typeof OOSA_REASON]

export const OOSA_REASON_VALUES = [
  OOSA_REASON.VACATION,
  OOSA_REASON.VISITING_FAMILY,
  OOSA_REASON.MEDICAL,
  OOSA_REASON.OTHER,
] as const satisfies readonly OosaReason[]

export const OOSA_REASON_LABELS: Record<OosaReason, string> = {
  [OOSA_REASON.VACATION]: 'Vacation',
  [OOSA_REASON.VISITING_FAMILY]: 'Visiting family',
  [OOSA_REASON.MEDICAL]: 'Medical',
  [OOSA_REASON.OTHER]: 'Other',
}

export function oosaReasonFromLabel(label: string): OosaReason | undefined {
  return OOSA_REASON_VALUES.find(
    (reason) => OOSA_REASON_LABELS[reason] === label
  )
}

export const OOSA_OTHER_REASON_PREFIX = 'Other: '

export function formatStoredOosaReason(
  reason: OosaReason,
  otherReason = ''
): string {
  if (reason !== OOSA_REASON.OTHER) {
    return OOSA_REASON_LABELS[reason]
  }
  const specified = otherReason.trim()
  return specified
    ? `${OOSA_OTHER_REASON_PREFIX}${specified}`
    : OOSA_REASON_LABELS[OOSA_REASON.OTHER]
}

export function parseStoredOosaReason(
  stored: string
): { reason: OosaReason; otherReason: string } | undefined {
  if ((OOSA_REASON_VALUES as readonly string[]).includes(stored)) {
    return { reason: stored as OosaReason, otherReason: '' }
  }
  const fromLabel = oosaReasonFromLabel(stored)
  if (fromLabel) {
    return { reason: fromLabel, otherReason: '' }
  }
  if (stored.startsWith(OOSA_OTHER_REASON_PREFIX)) {
    return {
      reason: OOSA_REASON.OTHER,
      otherReason: stored.slice(OOSA_OTHER_REASON_PREFIX.length).trim(),
    }
  }
  return undefined
}

export function formatOosaReasonDisplay(
  reason: OosaReason,
  otherReason = ''
): string {
  const specified = otherReason.trim()
  if (reason === OOSA_REASON.OTHER && specified) {
    return `${OOSA_REASON_LABELS[OOSA_REASON.OTHER]} (${specified})`
  }
  return OOSA_REASON_LABELS[reason]
}

export const CRITICAL_ATTENTION_REASON = {
  END_OF_LIFE: 'end-of-life',
  SNF_LEVEL_OF_CARE: 'snf-level-of-care',
  MULTIPLE_FALLS: 'multiple-falls',
  // Stored on flags created before the Figma reason list landed.
  CRITICAL_ILLNESS: 'critical-illness',
} as const
export type CriticalAttentionReason =
  (typeof CRITICAL_ATTENTION_REASON)[keyof typeof CRITICAL_ATTENTION_REASON]

export const CRITICAL_ATTENTION_REASON_VALUES = [
  CRITICAL_ATTENTION_REASON.END_OF_LIFE,
  CRITICAL_ATTENTION_REASON.SNF_LEVEL_OF_CARE,
  CRITICAL_ATTENTION_REASON.MULTIPLE_FALLS,
] as const satisfies readonly CriticalAttentionReason[]

export const CRITICAL_ATTENTION_REASON_LABELS: Record<
  CriticalAttentionReason,
  string
> = {
  [CRITICAL_ATTENTION_REASON.END_OF_LIFE]: 'End of life',
  [CRITICAL_ATTENTION_REASON.SNF_LEVEL_OF_CARE]: 'SNF level of care',
  [CRITICAL_ATTENTION_REASON.MULTIPLE_FALLS]:
    'Multiple falls (at least 1/month)',
  [CRITICAL_ATTENTION_REASON.CRITICAL_ILLNESS]: 'Critical Illness',
}

export function criticalAttentionReasonFromLabel(
  label: string
): CriticalAttentionReason | undefined {
  return (
    Object.keys(CRITICAL_ATTENTION_REASON_LABELS) as CriticalAttentionReason[]
  ).find((reason) => CRITICAL_ATTENTION_REASON_LABELS[reason] === label)
}

export function formatStoredCriticalAttentionReason(
  reason: CriticalAttentionReason
): string {
  return CRITICAL_ATTENTION_REASON_LABELS[reason]
}

export function parseStoredCriticalAttentionReason(
  stored: string
): CriticalAttentionReason | undefined {
  if (stored in CRITICAL_ATTENTION_REASON_LABELS) {
    return stored as CriticalAttentionReason
  }
  return criticalAttentionReasonFromLabel(stored)
}

export function formatCriticalAttentionReasonDisplay(
  reason: CriticalAttentionReason
): string {
  return CRITICAL_ATTENTION_REASON_LABELS[reason]
}

export const ISOLATION_REQUIRED_REASON = {
  BUG_INFESTATION: 'bug-infestation',
  CONTAGIOUS_FROM_ILLNESS: 'contagious-from-illness',
  SUSPECTED_EXPOSURE: 'suspected-exposure',
} as const
export type IsolationRequiredReason =
  (typeof ISOLATION_REQUIRED_REASON)[keyof typeof ISOLATION_REQUIRED_REASON]

export const ISOLATION_REQUIRED_REASON_VALUES = [
  ISOLATION_REQUIRED_REASON.BUG_INFESTATION,
  ISOLATION_REQUIRED_REASON.CONTAGIOUS_FROM_ILLNESS,
  ISOLATION_REQUIRED_REASON.SUSPECTED_EXPOSURE,
] as const satisfies readonly IsolationRequiredReason[]

export const ISOLATION_REQUIRED_REASON_LABELS: Record<
  IsolationRequiredReason,
  string
> = {
  [ISOLATION_REQUIRED_REASON.BUG_INFESTATION]: 'Bug infestation',
  [ISOLATION_REQUIRED_REASON.CONTAGIOUS_FROM_ILLNESS]:
    'Contagious from illness',
  [ISOLATION_REQUIRED_REASON.SUSPECTED_EXPOSURE]: 'Suspected exposure',
}

export function isolationRequiredReasonFromLabel(
  label: string
): IsolationRequiredReason | undefined {
  return (
    Object.keys(ISOLATION_REQUIRED_REASON_LABELS) as IsolationRequiredReason[]
  ).find((reason) => ISOLATION_REQUIRED_REASON_LABELS[reason] === label)
}

export function formatStoredIsolationRequiredReason(
  reason: IsolationRequiredReason
): string {
  return ISOLATION_REQUIRED_REASON_LABELS[reason]
}

export function parseStoredIsolationRequiredReason(
  stored: string
): IsolationRequiredReason | undefined {
  if (stored in ISOLATION_REQUIRED_REASON_LABELS) {
    return stored as IsolationRequiredReason
  }
  return isolationRequiredReasonFromLabel(stored)
}

export function formatIsolationRequiredReasonDisplay(
  reason: IsolationRequiredReason
): string {
  return ISOLATION_REQUIRED_REASON_LABELS[reason]
}
